import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { t } from 'i18next';

import { ReactComponent as ArrowUp } from '../../../../../../assets/icons/v3/up-arrow.svg';
import BdsAvatar from '../../../../../../shared/components/v3/BdsAvatar/BdsAvatar';
import BdsCardContainer from '../../../../../../shared/components/v3/Cards/BdsCardContainer/BdsCardContainer';
import EmptyCard from '../../../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import ErrorCard from '../../../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import TabBar from '../../../../../../shared/components/v3/TabBars/TabBar/TabBar';
import StyledTable from '../../../../../../shared/components/v3/Table/StyledTable/StyledTable';
import { CHALLENGE_RANKING_PAGE_SIZE, CHALLENGE_RANKING_TYPES } from '../../../../../../shared/constants/metrics';
import { CLICK_ACTION, SELECT_RANKING_CATEGORY } from '../../../../../../shared/constants/tagManager';
import { useInView } from '../../../../../../shared/hooks/useInView/useInView';
import {
  IMetricsChallengeRanking,
  IMetricsChallengeRankingEntry
} from '../../../../../../shared/interfaces/metrics';
import { ITableColumn } from '../../../../../../shared/interfaces/tables';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_METRICS_NE } from '../../../../../../shared/translations/metrics';
import { toLocaleNumberFormat } from '../../../../../../shared/utils/utils';

import './ChallengesRankingCard.scss';

interface IChallengesRankingCard {
  data: IMetricsChallengeRanking[];
  loading: boolean;
  error: boolean;
  onRetry?: () => void;
}

type SortableKey = 'value' | 'previousValue' | 'variationPct';

interface ISortState {
  key: SortableKey;
  isAscending: boolean;
}

const RANKING_TEXTS = TRANSLATIONS_METRICS_NE.challenges.ranking;

const compareRankingEntries =
  ({ key, isAscending }: ISortState) =>
  (a: IMetricsChallengeRankingEntry, b: IMetricsChallengeRankingEntry): number => {
    const first = a[key];
    const second = b[key];
    if (first === null || second === null || first === second) {
      if (first === second) return a.rank - b.rank;
      return first === null ? 1 : -1;
    }
    return isAscending ? first - second : second - first;
  };

const formatValue = (value: number | null): string => (value === null ? '—' : toLocaleNumberFormat(value));

const renderCollaborator = (entry: IMetricsChallengeRankingEntry) => (
  <div className="challenges-ranking-collaborator">
    <BdsAvatar size="s" src={{ initials: entry.name ?? '' }} />
    {entry.name ? (
      <span className="challenges-ranking-name">{entry.name}</span>
    ) : (
      <span className="challenges-ranking-name placeholder">{t(RANKING_TEXTS.noName)}</span>
    )}
  </div>
);

const renderVariation = ({ variationPct }: IMetricsChallengeRankingEntry) => {
  if (variationPct === null) {
    return <span className="challenges-ranking-variation neutral">{t(RANKING_TEXTS.new)}</span>;
  }

  let trend = 'neutral';
  if (variationPct > 0) trend = 'up';
  if (variationPct < 0) trend = 'down';
  const sign = variationPct > 0 ? '+' : '';

  return (
    <span className={`challenges-ranking-variation ${trend}`}>
      {trend !== 'neutral' && <ArrowUp className="challenges-ranking-trend" />}
      {`${sign}${toLocaleNumberFormat(variationPct, { maximumFractionDigits: 2 })}%`}
    </span>
  );
};

const RANKING_COLUMNS: ITableColumn[] = [
  {
    name: RANKING_TEXTS.columns.position,
    key: 'rank',
    class: 'challenges-ranking-col-rank',
    render: (entry: IMetricsChallengeRankingEntry) => <span className="challenges-ranking-rank">{entry.rank}</span>
  },
  {
    name: RANKING_TEXTS.columns.collaborator,
    key: 'name',
    class: 'challenges-ranking-col-name',
    render: renderCollaborator
  },
  {
    name: RANKING_TEXTS.columns.currentMonth,
    key: 'value',
    class: 'challenges-ranking-col-number',
    isSortable: true,
    render: (entry: IMetricsChallengeRankingEntry) => (
      <span className="challenges-ranking-current">{formatValue(entry.value)}</span>
    )
  },
  {
    name: RANKING_TEXTS.columns.previousMonth,
    key: 'previousValue',
    class: 'challenges-ranking-col-number',
    isSortable: true,
    render: (entry: IMetricsChallengeRankingEntry) => (
      <span className="challenges-ranking-previous">{formatValue(entry.previousValue)}</span>
    )
  },
  {
    name: RANKING_TEXTS.columns.variation,
    key: 'variationPct',
    class: 'challenges-ranking-col-number',
    isSortable: true,
    render: renderVariation
  }
];

const ChallengesRankingCard = ({ data, loading, error, onRetry }: IChallengesRankingCard) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [sort, setSort] = useState<ISortState>();
  const [visibleCount, setVisibleCount] = useState<number>(CHALLENGE_RANKING_PAGE_SIZE);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);

  const series = useMemo(() => data.filter((item) => CHALLENGE_RANKING_TYPES[item.type]), [data]);
  const active = series[activeIndex] ?? series[0];

  const sortedEntries = useMemo(() => {
    const entries = active?.entries ?? [];
    return sort ? [...entries].sort(compareRankingEntries(sort)) : entries;
  }, [active, sort]);

  const hasMore = visibleCount < sortedEntries.length;
  const { ref: sentinelRef, enterCount } = useInView<HTMLDivElement>({
    root: scrollElement,
    threshold: 0,
    enabled: hasMore
  });

  const resetList = useCallback(() => {
    setVisibleCount(CHALLENGE_RANKING_PAGE_SIZE);
    if (scrollElement) scrollElement.scrollTop = 0;
  }, [scrollElement]);

  useEffect(() => {
    if (enterCount > 0) {
      setVisibleCount((count) => count + CHALLENGE_RANKING_PAGE_SIZE);
    }
  }, [enterCount]);

  useEffect(() => {
    setSort(undefined);
    resetList();
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeTab = (index: number) => {
    setActiveIndex(index);
    setSort(undefined);
    resetList();
    TagManagerService.pushEvent(CLICK_ACTION, SELECT_RANKING_CATEGORY, {
      option: series[index]?.type,
      tab: 'challenges'
    });
  };

  const sortData = (key: SortableKey, isAscending: boolean) => {
    setSort({ key, isAscending });
    resetList();
  };

  const renderHeader = () => (
    <div className="challenges-ranking-header">
      <h3 className="challenges-ranking-title">{t(RANKING_TEXTS.title)}</h3>
      {!error && series.length > 0 && (
        <div className="challenges-ranking-tabs">
          <TabBar
            items={series.map((item) => ({
              icon: CHALLENGE_RANKING_TYPES[item.type].icon,
              value: CHALLENGE_RANKING_TYPES[item.type].label
            }))}
            activeIndex={series.indexOf(active)}
            action={changeTab}
          />
        </div>
      )}
    </div>
  );

  const renderBody = () => {
    if (error) {
      return (
        <ErrorCard
          primaryText={t(TRANSLATIONS_METRICS_NE.challenges.error.title)}
          secondaryText={t(TRANSLATIONS_METRICS_NE.challenges.error.description)}
          buttonText={t(TRANSLATIONS_METRICS_NE.challenges.retry)}
          action={onRetry}
        />
      );
    }

    if (sortedEntries.length === 0) {
      return (
        <EmptyCard
          center
          className="challenges-ranking-empty"
          primaryText={t(RANKING_TEXTS.emptyState.title)}
          secondaryText={t(RANKING_TEXTS.emptyState.description)}
        />
      );
    }

    return (
      <div className="challenges-ranking-scroll" ref={setScrollElement} data-testid="challenges-ranking-scroll">
        <StyledTable
          key={active.type}
          columns={RANKING_COLUMNS}
          data={sortedEntries.slice(0, visibleCount)}
          dataKey="rank"
          sortData={sortData}
          showShadowHeader={false}
        />
        {hasMore && <div ref={sentinelRef} className="challenges-ranking-sentinel" aria-hidden />}
      </div>
    );
  };

  if (loading) {
    return <Skeleton className="rounded-4xl" variant="rectangular" width="100%" height={520} />;
  }

  return (
    <BdsCardContainer className="challenges-ranking-card" paddingSize="padding-l" borderRadiusSize="radius-l">
      {renderHeader()}
      {renderBody()}
    </BdsCardContainer>
  );
};

export default ChallengesRankingCard;
